import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { FileBadge2, ExternalLink } from "lucide-react";
import {
  useCreateInstallationCertificateMutation,
  useGetInstallationCertificateQuery,
} from "../../redux/admin/adminApi";
import { useStatus } from "../../popup/NotificationContext";

const DEFAULT_FORM = {
  engineNo: "",
  chassisNo: "",
  ownerName: "",
  ownerAddress: "",
  ownerMobileNo: "",
  ownerIdNo: "",
  distributorName: "",
  distributorAddress: "",
  distributorMobileNo: "",
};

function getBackendApiBase() {
  return process.env.REACT_APP_BACKEND_URL || "http://localhost:7600/api";
}

export default function InstallationCertificateModal({
  open,
  onClose,
  device,
}) {
  const { showStatus } = useStatus();
  const deviceId = device?._id || device?.id;
  const { data, isFetching } = useGetInstallationCertificateQuery(deviceId, {
    skip: !open || !deviceId,
  });
  const [createInstallationCertificate, { isLoading }] = useCreateInstallationCertificateMutation();

  const [form, setForm] = useState(DEFAULT_FORM);
  const [logoFile, setLogoFile] = useState(null);
  const [existingPdfUrl, setExistingPdfUrl] = useState("");

  const backendApiBase = useMemo(() => getBackendApiBase(), []);

  useEffect(() => {
    if (!open) return;

    const certificate = data?.certificate || device?.installationCertificate || {};
    setForm({
      engineNo: certificate.engineNo || "",
      chassisNo: certificate.chassisNo || "",
      ownerName: certificate.ownerName || "",
      ownerAddress: certificate.ownerAddress || "",
      ownerMobileNo: certificate.ownerMobileNo || "",
      ownerIdNo: certificate.ownerIdNo || "",
      distributorName: certificate.distributorName || "",
      distributorAddress: certificate.distributorAddress || "",
      distributorMobileNo: certificate.distributorMobileNo || "",
    });
    setExistingPdfUrl(certificate.pdfUrl || "");
    setLogoFile(null);
  }, [data, device, open]);

  const openPdf = async () => {
    if (!deviceId) return;

    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    if (!token) {
      showStatus("error", "Login token missing.");
      return;
    }

    try {
      const response = await fetch(`${backendApiBase}/devices/${deviceId}/installation-certificate/file`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to open PDF.");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      showStatus("error", error?.message || "Failed to open PDF.");
    }
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!deviceId) return;

    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        formData.append(key, value || "");
      });
      if (logoFile) {
        formData.append("logo", logoFile);
      }

      const response = await createInstallationCertificate({
        deviceId,
        formData,
      }).unwrap();

      const pdfUrl = response?.pdfUrl || "";
      setExistingPdfUrl(pdfUrl);
      showStatus("success", "Installation certificate generated.");
      await openPdf();
    } catch (error) {
      showStatus("error", error?.data?.msg || "Failed to generate installation certificate.");
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={isLoading ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 900, display: "flex", alignItems: "center", gap: 1 }}>
        <FileBadge2 size={20} />
        Create Installation Certificate
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              mb: 2,
              backgroundColor: "rgba(59,130,246,0.03)",
            }}
          >
            <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Device details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Vehicle No" value={device?.name || ""} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="IMEI" value={device?.imei || ""} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="SIM No" value={device?.msdn || ""} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Device Model" value={device?.model || ""} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Valid Upto"
                  value={data?.device?.deviceLicense?.expiryDate ? new Date(data.device.deviceLicense.expiryDate).toLocaleDateString("en-GB") : ""}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Engine No" value={form.engineNo} onChange={(e) => setField("engineNo", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Chassis No" value={form.chassisNo} onChange={(e) => setField("chassisNo", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Owner Name" value={form.ownerName} onChange={(e) => setField("ownerName", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Owner Address" value={form.ownerAddress} onChange={(e) => setField("ownerAddress", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Owner Mobile No" value={form.ownerMobileNo} onChange={(e) => setField("ownerMobileNo", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Owner ID No" value={form.ownerIdNo} onChange={(e) => setField("ownerIdNo", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Distributor Name" value={form.distributorName} onChange={(e) => setField("distributorName", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Distributor Address" value={form.distributorAddress} onChange={(e) => setField("distributorAddress", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Distributor Mobile No" value={form.distributorMobileNo} onChange={(e) => setField("distributorMobileNo", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button variant="outlined" component="label">
                Upload Logo
                <input
                  hidden
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                />
              </Button>
              <Typography sx={{ mt: 1, color: "text.secondary", fontSize: 13 }}>
                {logoFile?.name || "Optional logo image"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              {existingPdfUrl ? (
                <Button
                  variant="text"
                  startIcon={<ExternalLink size={16} />}
                  onClick={() => openPdf()}
                >
                  Open last generated PDF
                </Button>
              ) : null}
            </Grid>
          </Grid>

          {isFetching ? (
            <Typography sx={{ mt: 2, color: "text.secondary" }}>
              Loading latest certificate details...
            </Typography>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? "Generating..." : "Generate PDF"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
